import getTranslation from 'bdata-sns-shared/utils/getTranslation';
import menOnBaseText from '../../../../utils/getAccessibilityText';

const atBats = (dataSource) => dataSource?.stats?.[0]?.stats?.atBats;

const atBatString = (dataSource, dictionary) => getTranslation(
    dictionary,
    'reader_at_bats',
    `${dataSource?.stats?.[0]?.stats?.hits} for ${dataSource?.stats?.[0]?.stats?.atBats}, `,
    { hits: dataSource?.stats?.[0]?.stats?.hits, atBats: atBats(dataSource) }
);

const noAtBatsString = (dictionary) => getTranslation(
    dictionary,
    'reader_no_at_bats',
    'No at bats'
);

export const previewAwayPitcher = (awayTeam, isPostponed, dictionary) => {
    const isAwayPitcher = awayTeam?.probablePitcher?.fullName;
    const awayTeamShortName = awayTeam?.team?.shortName || '';

    const fullName = awayTeam?.probablePitcher?.fullName;
    const wins = awayTeam?.probablePitcher?.stats?.[3]?.stats?.wins;
    const losses = awayTeam?.probablePitcher?.stats?.[3]?.stats?.losses;
    const era = awayTeam?.probablePitcher?.stats?.[3]?.stats?.era;

    const handleHand = (code) => {
        if (code === 'L') {
            return getTranslation(dictionary, 'reader_lh_pitcher', 'Left handed pitcher');
        } else if (code === 'R') {
            return getTranslation(dictionary, 'reader_rh_pitcher', 'Right handed pitcher');
        }
    };

    const awayProbable = isAwayPitcher && !isPostponed
        ? getTranslation(
            dictionary,
            'reader_player_away_probable',
            `${handleHand(awayTeam?.probablePitcher?.pitchHand?.code)} ${fullName}, ${wins} win${wins !== 1 ? 's' : ','} and ${losses} loss${losses !== 1 ? 'es' : ','}, ${era} ERA. `,
            {
                awayPitcherHand: handleHand(awayTeam?.probablePitcher?.pitchHand?.code),
                awayProbableName: fullName,
                awayProbableWins: wins,
                winsPlural: wins !== 1 ? 's,' : ',',
                awayProbableLosses: losses,
                lossPlural: losses !== 1 ? 'es,' : ',',
                awayProbableEra: era
            }
        )
        : getTranslation(dictionary, 'reader_tbd', 'To be decided');

    const previewAwayPitcher = getTranslation(
        dictionary,
        'reader_player_preview_away',
        `Pitching for ${awayTeamShortName}, ${awayProbable}.`,
        { awayTeam: awayTeamShortName, awayProbable }
    );

    return previewAwayPitcher;
};

export const previewHomePitcher = (homeTeam, isPostponed, dictionary) => {
    const isHomePitcher = homeTeam?.probablePitcher?.fullName;
    const homeTeamShortName = homeTeam?.team?.shortName || '';

    const fullName = homeTeam?.probablePitcher?.fullName;
    const wins = homeTeam?.probablePitcher?.stats?.[3]?.stats?.wins;
    const losses = homeTeam?.probablePitcher?.stats?.[3]?.stats?.losses;
    const era = homeTeam?.probablePitcher?.stats?.[3]?.stats?.era;

    const handleHand = (code) => {
        if (code === 'L') {
            return getTranslation(dictionary, 'reader_lh_pitcher', 'Left handed pitcher');
        } else if (code === 'R') {
            return getTranslation(dictionary, 'reader_rh_pitcher', 'Right handed pitcher');
        }
    };

    const homeProbable = isHomePitcher && !isPostponed
        ? getTranslation(
            dictionary,
            'reader_player_home_probable',
            `${handleHand(homeTeam?.probablePitcher?.pitchHand?.code)} ${fullName}, ${wins} win${wins !== 1 ? 's' : ','} and ${losses} loss${losses !== 1 ? 'es' : ','}, ${era} ERA. `,
            {
                homePitcherHand: handleHand(homeTeam?.probablePitcher?.pitchHand?.code),
                homeProbableName: fullName,
                homeProbableWins: wins,
                winsPlural: wins !== 1 ? 's,' : ',',
                homeProbableLosses: losses,
                lossPlural: losses !== 1 ? 'es,' : ',',
                homeProbableEra: era
            }
        )
        : getTranslation(dictionary, 'reader_tbd', 'To be decided');

    const previewHomePitcher = getTranslation(
        dictionary,
        'reader_player_preview_home',
        `Pitching for ${homeTeamShortName}, ${homeProbable}.`,
        { homeTeam: homeTeamShortName, homeProbable }
    );

    return previewHomePitcher;
};

export const battingNextString = (dictionary, offenseTeamName, dueUpConfig) => {
    const batter = dueUpConfig?.batter;
    const batterName = batter?.fullName;

    return getTranslation(
        dictionary,
        'reader_player_batting_next',
        `Due up for ${offenseTeamName}: Batting next: ${batterName}, ${atBats(batter) ? atBatString(batter, dictionary) : noAtBatsString(dictionary)}`,
        {
            offenseTeamName,
            batterName,
            atBats: atBats(batter) ? atBatString(batter, dictionary) : noAtBatsString(dictionary)
        }
    );
};

export const onDeckString = (dictionary, offenseTeamName, dueUpConfig) => {
    const onDeck = dueUpConfig?.onDeck;
    const onDeckName = onDeck?.fullName;

    return getTranslation(
        dictionary,
        'reader_player_ondeck',
        `On deck: ${onDeckName}, ${atBats(onDeck) ? atBatString(onDeck, dictionary) : noAtBatsString(dictionary)}`,
        {
            offenseTeamName,
            batterName: onDeckName,
            atBats: atBats(onDeck) ? atBatString(onDeck, dictionary) : noAtBatsString(dictionary)
        }
    );
};

export const inTheHoleString = (dictionary, offenseTeamName, dueUpConfig) => {
    const inHole = dueUpConfig?.inHole;
    const inHoleName = inHole?.fullName;

    return getTranslation(
        dictionary,
        'reader_player_inhole',
        `In the hole: ${inHoleName}, ${atBats(inHole) ? atBatString(inHole, dictionary) : noAtBatsString(dictionary)}`,
        {
            offenseTeamName,
            batterName: inHoleName,
            atBats: atBats(inHole) ? atBatString(inHole, dictionary) : noAtBatsString(dictionary)
        }
    );
};

export const livePitcherString = (dictionary, defenseTeamName, defense) => {
    const pitcherName = defense?.pitcher?.fullName;
    const inningsPitched = defense?.pitcher?.stats?.[1]?.stats?.inningsPitched;
    const pitcherEra = defense?.pitcher?.stats?.[3]?.stats?.era;

    return getTranslation(
        dictionary,
        'reader_player_pitcher_string',
        `Pitching for ${defenseTeamName}: ${pitcherName}, ${inningsPitched} innings pitched, ${pitcherEra} ERA.`,
        {
            defenseTeamName,
            pitcherName,
            inningsPitched,
            pitcherEra
        }
    );
};

export const liveBatterString = (dictionary, offenseTeamName, offense, linescore) => {
    const batter = offense?.batter;
    const batterFullName = batter?.fullName;
    const batterStats = linescore?.offense?.batter;
    const batterOps = batterStats?.stats?.[2]?.stats?.ops || batterStats?.stats?.[0]?.stats?.ops;

    return getTranslation(
        dictionary,
        'reader_player_batter_string',
        `Batting for ${offenseTeamName}: ${batterFullName}, ${atBats(batterStats) ? atBatString(batterStats, dictionary) : noAtBatsString(dictionary)} ${batterOps} O.P.S.`,
        {
            offenseTeamName,
            batterName: batterFullName,
            atBats: atBats(batterStats) ? atBatString(batterStats, dictionary) : noAtBatsString(dictionary),
            batterOps
        }
    );
};

export const countString = (dictionary, balls, strikes) => {
    return getTranslation(
        dictionary,
        'reader_count',
        `The count is ${balls} and ${strikes}.`,
        { balls, strikes }
    );
};

export const gameDataString = (dictionary, linescore) => {
    const {
        outs,
        offense = {},
        balls,
        strikes
    } = linescore;
    const { first, second, third } = offense;
    const basepaths = { first: !!first, second: !!second, third: !!third };

    const menOnBase = menOnBaseText(basepaths);
    const playersOnBaseString = getTranslation(dictionary, menOnBase.dictKeys, menOnBase.text);

    return getTranslation(
        dictionary,
        'reader_player_game_data',
        `${outs} out. ${playersOnBaseString}, ${countString(dictionary, balls, strikes)}`,
        { outs, playersOnBaseString, countString: countString(dictionary, balls, strikes) }
    );
};

export const winningPitcherString = (dictionary, decisions) => {
    const winner = decisions?.winner;
    if (!winner) {
        return ' ';
    }

    const stats = winner?.stats?.[3]?.stats;
    const fullName = winner?.fullName;
    const wins = stats?.wins;
    const losses = stats?.losses;
    const era = stats?.era;

    const hasStats = !!stats;
    const translationKey = hasStats ? 'reader_player_winning_pitcher' : 'reader_player_winning_pitcher_name_only';

    const defaultMessage = hasStats
        ? `Winning pitcher: ${fullName}, ${wins} win${wins !== 1 ? 's' : ','} and ${losses} loss${losses !== 1 ? 'es' : ','} ${era} ERA.`
        : `Winning pitcher: ${fullName}.`;

    const translationData = hasStats
        ? {
            winningPitcher: fullName,
            winningPitcherWins: wins,
            winsPlural: wins !== 1 ? 's' : ',',
            winningPitcherLosses: losses,
            lossPlural: losses !== 1 ? 'es' : ',',
            winningPitcherEra: era
        }
        : { winningPitcher: fullName };

    return getTranslation(dictionary, translationKey, defaultMessage, translationData);
};

export const losingPitcherString = (dictionary, decisions) => {
    const loser = decisions?.loser;
    if (!loser) {
        return ' ';
    }

    const stats = loser?.stats?.[3]?.stats;
    const fullName = loser?.fullName;
    const wins = stats?.wins;
    const losses = stats?.losses;
    const era = stats?.era;

    const hasStats = !!stats;
    const translationKey = hasStats ? 'reader_player_losing_pitcher' : 'reader_player_losing_pitcher_name_only';

    const defaultMessage = hasStats
        ? `Losing pitcher: ${fullName}, ${wins} win${wins !== 1 ? 's' : ','} and ${losses} loss${losses !== 1 ? 'es' : ','} ${era} ERA.`
        : `Losing pitcher: ${fullName}.`;

    const translationData = hasStats
        ? {
            losingPitcher: fullName,
            losingPitcherWins: wins,
            winsPlural: wins !== 1 ? 's' : ',',
            losingPitcherLosses: losses,
            lossPlural: losses !== 1 ? 'es' : ',',
            losingPitcherEra: era
        }
        : { losingPitcher: fullName };

    return getTranslation(dictionary, translationKey, defaultMessage, translationData);
};

export const savingPitcherString = (dictionary, decisions) => getTranslation(
    dictionary,
    'reader_player_saving_pitcher',
    `Save: ${decisions?.save?.fullName}, ${decisions?.save?.stats?.[3]?.stats?.saves} saves`,
    {
        savingPitcher: decisions?.save?.fullName,
        numOfSaves: decisions?.save?.stats?.[3]?.stats?.saves
    }
);
